// Create-account page
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight } from "lucide-react";
import { useCustomerAuth, startGoogleSignIn } from "@/lib/customerAuth";

export default function SignUp() {
    const { user, signup } = useCustomerAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", marketing_consent: false, accept: false });
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (user) nav("/account", { replace: true }); }, [user, nav]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.accept) return toast.error("Please accept the terms & privacy policy");
        setBusy(true);
        try {
            await signup({
                name: form.name.trim(),
                email: form.email.trim(),
                password: form.password,
                marketing_consent: form.marketing_consent,
            });
            toast.success("Welcome to Serenity Cove. Check your inbox to verify your email.");
            nav("/account", { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not create your account");
        } finally { setBusy(false); }
    };

    return (
        <section data-testid="signup-page" className="min-h-screen bg-ivory pt-24 pb-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 40%, #d4af37 0%, transparent 40%), radial-gradient(circle at 80% 60%, #0b3a2e 0%, transparent 45%)" }} />
            <div className="relative max-w-md mx-auto px-6">
                <div className="text-center mb-10">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-gold">Serenity Cove</div>
                    <h1 className="mt-4 font-display text-4xl md:text-5xl text-navy">Create account</h1>
                    <p className="mt-3 text-sm text-navy/60">Save your stays, earn Cove points, unlock member perks.</p>
                </div>

                <form onSubmit={submit} className="bg-white border border-navy/10 rounded-2xl p-8 shadow-sm space-y-5" data-testid="signup-form">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Full name</span>
                        <div className="mt-2 relative">
                            <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/30" />
                            <input type="text" required data-testid="signup-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full pl-9 pr-3 py-3 border border-navy/15 rounded-lg text-sm focus:border-gold focus:outline-none" />
                        </div>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Email</span>
                        <div className="mt-2 relative">
                            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/30" />
                            <input type="email" required autoComplete="email" data-testid="signup-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="w-full pl-9 pr-3 py-3 border border-navy/15 rounded-lg text-sm focus:border-gold focus:outline-none" />
                        </div>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Password (min. 8)</span>
                        <div className="mt-2 relative">
                            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/30" />
                            <input type="password" required minLength={8} autoComplete="new-password" data-testid="signup-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                                className="w-full pl-9 pr-3 py-3 border border-navy/15 rounded-lg text-sm focus:border-gold focus:outline-none" />
                        </div>
                    </label>

                    <div className="space-y-2">
                        <label className="flex items-start gap-3 text-[12px] text-navy/70 cursor-pointer">
                            <input type="checkbox" required data-testid="signup-accept" checked={form.accept} onChange={(e) => setForm({ ...form, accept: e.target.checked })} className="mt-1 accent-navy" />
                            <span>I agree to the <a href="#" className="text-gold hover:underline">Terms of Service</a> and <a href="#" className="text-gold hover:underline">Privacy Policy</a>.</span>
                        </label>
                        <label className="flex items-start gap-3 text-[12px] text-navy/70 cursor-pointer">
                            <input type="checkbox" data-testid="signup-marketing" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="mt-1 accent-navy" />
                            <span>Send me occasional Serenity Cove offers and stories. (Optional — you can unsubscribe anytime.)</span>
                        </label>
                    </div>

                    <button type="submit" disabled={busy} data-testid="signup-submit" className="w-full inline-flex items-center justify-center gap-2 bg-navy text-ivory px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] hover:bg-navy/90 disabled:opacity-60">
                        {busy ? "Creating…" : "Create account"} <ArrowRight size={13} />
                    </button>

                    <div className="relative py-1">
                        <div className="border-t border-navy/10" />
                        <span className="absolute inset-0 flex items-center justify-center">
                            <span className="bg-white px-3 text-[10px] uppercase tracking-[0.22em] text-navy/50">or</span>
                        </span>
                    </div>

                    <button type="button" onClick={startGoogleSignIn} data-testid="signup-google" className="w-full inline-flex items-center justify-center gap-3 bg-white border border-navy/20 text-navy px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.16em] hover:border-gold hover:bg-navy/[.02]">
                        <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.44c-.29 1.48-1.14 2.73-2.4 3.58v3h3.87c2.26-2.09 3.58-5.17 3.58-8.82z"/><path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.91l-3.87-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"/><path fill="#FBBC05" d="M5.27 14.29c-.24-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"/><path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"/></svg>
                        Continue with Google
                    </button>
                </form>

                <p className="mt-6 text-center text-sm text-navy/60">
                    Already have an account?{" "}
                    <Link to="/signin" className="text-gold hover:underline" data-testid="signup-to-signin">Sign in</Link>
                </p>
            </div>
        </section>
    );
}
